/**
 * SGTR Slideshow Fix
 *
 * Fixes two Dawn theme slideshow problems:
 * 1. Desktop: mouse-wheel scrolling over the slideshow gets hijacked by the
 *    slider's horizontal scroll-snap, making the page feel stuck.
 * 2. Mobile: fast swipes skip past 2-3 slides at once instead of landing on
 *    exactly the next one.
 *
 * This does not replace Dawn's slideshow-component.js — it only patches
 * scroll/touch handling on top of it.
 */
(function () {
  'use strict';

  function initSlideshowFix(root) {
    var slider = root.querySelector('.slider');
    if (!slider) return;

    /* ------------------------------------------------------------------
       FIX 1 — Desktop wheel scroll
       Vertical mouse-wheel movement should always scroll the page.
       Only forward the event to the slider when the gesture is clearly
       horizontal (shift+wheel or a trackpad horizontal swipe), which is
       the only case a user actually means to move the slider with a wheel.
    ------------------------------------------------------------------ */
    slider.addEventListener(
      'wheel',
      function (e) {
        var isHorizontalIntent = Math.abs(e.deltaX) > Math.abs(e.deltaY);

        if (!isHorizontalIntent) {
          // Let the vertical scroll pass straight through to the page.
          // Do NOT preventDefault, do NOT touch scrollLeft.
          return;
        }

        // Genuine horizontal wheel/trackpad gesture — let the slider handle
        // it natively via scroll-snap, but stop it from also scrolling the
        // page diagonally.
        e.preventDefault();
        slider.scrollLeft += e.deltaX;
      },
      { passive: false }
    );

    /* ------------------------------------------------------------------
       FIX 2 — Mobile: one slide per swipe
       Track touch start/end X position ourselves. On release, snap to
       exactly the next or previous slide based on swipe direction, rather
       than trusting momentum scrolling to land on the right one.
    ------------------------------------------------------------------ */
    var slides = Array.prototype.slice.call(
      root.querySelectorAll('.slideshow__slide')
    );
    if (slides.length === 0) return;

    var touchStartX = 0;
    var touchStartScrollLeft = 0;
    var isTouching = false;

    function getCurrentIndex() {
      var sliderCenter = slider.scrollLeft + slider.clientWidth / 2;
      var closestIndex = 0;
      var closestDistance = Infinity;

      slides.forEach(function (slide, index) {
        var slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        var distance = Math.abs(sliderCenter - slideCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    function goToSlide(index) {
      var clamped = Math.max(0, Math.min(slides.length - 1, index));
      slides[clamped].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }

    slider.addEventListener(
      'touchstart',
      function (e) {
        isTouching = true;
        touchStartX = e.touches[0].clientX;
        touchStartScrollLeft = slider.scrollLeft;
        slider.classList.add('sgtr-dragging');
      },
      { passive: true }
    );

    slider.addEventListener(
      'touchend',
      function (e) {
        if (!isTouching) return;
        isTouching = false;
        slider.classList.remove('sgtr-dragging');

        var touchEndX = e.changedTouches[0].clientX;
        var deltaX = touchStartX - touchEndX;
        var swipeThreshold = 40; // px — below this, treat as a tap, not a swipe

        var currentIndex = getCurrentIndex();

        if (Math.abs(deltaX) < swipeThreshold) {
          // Not a real swipe — settle back on the nearest slide (undoes any
          // partial momentum scroll from a light touch).
          goToSlide(currentIndex);
          return;
        }

        if (deltaX > 0) {
          // Swiped left → advance exactly one slide forward
          goToSlide(currentIndex + 1);
        } else {
          // Swiped right → go back exactly one slide
          goToSlide(currentIndex - 1);
        }
      },
      { passive: true }
    );

    /* Same one-step lock for mouse drag on desktop (click-and-drag users) */
    var mouseIsDown = false;
    var mouseStartX = 0;

    slider.addEventListener('mousedown', function (e) {
      mouseIsDown = true;
      mouseStartX = e.clientX;
      slider.classList.add('sgtr-dragging');
    });

    window.addEventListener('mouseup', function (e) {
      if (!mouseIsDown) return;
      mouseIsDown = false;
      slider.classList.remove('sgtr-dragging');

      var deltaX = mouseStartX - e.clientX;
      var currentIndex = getCurrentIndex();
      var dragThreshold = 60;

      if (Math.abs(deltaX) < dragThreshold) {
        goToSlide(currentIndex);
        return;
      }

      goToSlide(deltaX > 0 ? currentIndex + 1 : currentIndex - 1);
    });

    /* Keep counter (if visible) in sync when it exists */
    var counterCurrent = root.querySelector('.slider-counter--current');

    /* ------------------------------------------------------------------
       Mobile dots — click to jump to a slide, and keep the active dot
       in sync whether the slide changed via swipe, arrow click, or
       autoplay (data-autoplay="true").
    ------------------------------------------------------------------ */
    var dots = Array.prototype.slice.call(
      root.querySelectorAll('.sgtr-mobile-dot')
    );

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var index = parseInt(dot.getAttribute('data-slide-index'), 10);
        goToSlide(index);
      });
    });

    function updateActiveDot() {
      if (dots.length === 0) return;
      var currentIndex = getCurrentIndex();
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === currentIndex);
      });
    }

    var updateNavState = function () {
      if (counterCurrent) counterCurrent.textContent = getCurrentIndex() + 1;
      updateActiveDot();
    };

    slider.addEventListener('scroll', function () {
      window.clearTimeout(slider._sgtrScrollTimeout);
      slider._sgtrScrollTimeout = window.setTimeout(updateNavState, 100);
    });
  }

  function init() {
    var slideshows = document.querySelectorAll('slideshow-component');
    slideshows.forEach(initSlideshowFix);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when the theme editor swaps sections in/out
  document.addEventListener('shopify:section:load', function (e) {
    var slideshow = e.target.querySelector('slideshow-component');
    if (slideshow) initSlideshowFix(slideshow);
  });
})();
